/**
 * EventFiltersBar - The shared filter row used by both the Calendar
 * and Map pages.
 *
 * Composes:
 *   - DateRangePicker (molecule)
 *   - MultiSelectFilter (molecule, used 4×: customer, tech, city, state)
 *
 * Owns no domain knowledge of how the events are fetched — the parent
 * page passes in the option lists for each filter and receives the
 * full filter state on every change. The same instance of this
 * component therefore works whether the underlying data feed is a
 * super-admin's full set or a tech's narrow assignment list.
 *
 * Filter state is a flat object (DateRangeValue + four Sets) so the
 * parent can pass it straight into the scheduledEventsService request
 * builder without intermediate massaging.
 */
import React from 'react';
import { DateRangePicker, MultiSelectFilter } from '../../molecules';
import type { DateRangeValue, MultiSelectOption } from '../../molecules';
import type { ScheduledEventRecordType } from '../../../services/scheduledEventsService';
import { ResponsiveFilters } from '../ResponsiveFilters';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * One option in an extensible toggle group (record types today, but
 * built so new toggle categories — e.g. priority, status — can be added
 * with the same molecule).
 *
 * `color` (optional) drives the selected-state background and border —
 * pass any valid CSS color. When omitted the option falls back to the
 * brand primary so unstyled toggle groups still look correct.
 */
export interface FilterToggleOption<T extends string = string> {
    value: T;
    label: string;
    color?: string;
}

/** The full filter state managed by the parent. */
export interface EventFiltersValue {
    dateRange: DateRangeValue;
    customerIds: Set<string>;
    assigneeIds: Set<string>;
    cities: Set<string>;
    states: Set<string>;
    /** Which record types to include. Toggle UI lives at the top of the bar. */
    recordTypes: Set<ScheduledEventRecordType>;
}

/* ============================================
 * INTERNAL: extensible toggle group
 * ============================================
 * A reusable checkbox-style pill group. Today only the record-type
 * filter uses it; future filters (e.g. status, priority) can drop in
 * additional <FilterToggleGroup> blocks alongside this one without
 * disturbing the multi-selects below.
 */

interface FilterToggleGroupProps<T extends string> {
    label: string;
    options: FilterToggleOption<T>[];
    selected: Set<T>;
    onChange: (next: Set<T>) => void;
}

function FilterToggleGroup<T extends string>({
    label,
    options,
    selected,
    onChange,
}: FilterToggleGroupProps<T>) {
    const toggle = (value: T) => {
        const next = new Set(selected);
        if (next.has(value)) {
            next.delete(value);
        } else {
            next.add(value);
        }
        onChange(next);
    };

    return (
        <div role="group" aria-label={label} className="event-filters-bar__group flex flex-col gap-4px">
            <span className="event-filters-bar__group-label text-11px font-sans font-medium uppercase tracking-wide text-brand-text-muted">
                {label}
            </span>
            <div className="event-filters-bar__chips flex flex-wrap gap-6px">
                {options.map((opt) => {
                    const isOn = selected.has(opt.value);
                    /* When the option declares a color, apply it via inline
                     * style for the selected state — Tailwind can't generate
                     * arbitrary colors at runtime, so we sidestep the class
                     * system here. Unselected pills use neutral chrome
                     * regardless of color so the row reads as a single group. */
                    const selectedStyle: React.CSSProperties | undefined =
                        isOn && opt.color
                            ? { backgroundColor: opt.color, borderColor: opt.color, color: '#ffffff' }
                            : undefined;
                    const iconStyle: React.CSSProperties | undefined =
                        isOn && opt.color ? { color: opt.color } : undefined;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            role="checkbox"
                            aria-checked={isOn}
                            onClick={() => toggle(opt.value)}
                            style={selectedStyle}
                            className={[
                                /* min-h-44px on touch — the calendar/map filter chips
                                   are 32px tall and sit shoulder to shoulder. */
                                'inline-flex items-center gap-6px px-10px py-6px min-h-44px desktop:min-h-0 rounded-full border text-12px font-sans font-medium transition-colors',
                                isOn
                                    ? opt.color
                                        ? '' /* colors come from inline style */
                                        : 'bg-brand-primary text-white border-brand-primary'
                                    : 'bg-white text-brand-text border-brand-border hover:bg-brand-surface',
                            ].join(' ')}
                        >
                            {/* Faux checkbox — gives the group its checkbox affordance
                             * without losing the pill's compact footprint. */}
                            <span
                                aria-hidden="true"
                                style={iconStyle}
                                className={[
                                    'inline-flex items-center justify-center w-12px h-12px rounded-sm border',
                                    isOn
                                        ? opt.color
                                            ? 'border-white bg-white' /* icon color via inline style */
                                            : 'border-white bg-white text-brand-primary'
                                        : 'border-brand-border',
                                ].join(' ')}
                            >
                                {isOn && (
                                    <svg viewBox="0 0 16 16" className="w-10px h-10px" aria-hidden="true">
                                        <path
                                            d="M3 8.5l3 3 7-7"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </span>
                            {opt.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* Toggle options exposed for the record-type filter. Colors match the
 * calendar chip palette in CalendarView.CATEGORY_COLORS so the toggle
 * acts as a visual legend in addition to a filter. Project work orders
 * (burnt-orange) aren't a separate toggle today because the spec only
 * asked for inspection vs work order, and they roll up under the Work
 * Orders toggle; the wire type doesn't distinguish them. */
const RECORD_TYPE_OPTIONS: FilterToggleOption<ScheduledEventRecordType>[] = [
    { value: 'work_order', label: 'Work Orders', color: 'var(--brand-primary, #F9550E)' },
    { value: 'inspection', label: 'Inspections', color: '#1D4ED8' },
];

/**
 * Props for EventFiltersBar.
 *
 * @property value           Current filter state (controlled)
 * @property onChange        Called with the next state on any field change
 * @property customerOptions Option list for the Customer multi-select
 * @property assigneeOptions Option list for the Tech / Assignee multi-select
 * @property cityOptions     Option list for the City multi-select
 * @property stateOptions    Option list for the State multi-select
 * @property className       Optional extra classes for the outer wrapper
 */
export interface EventFiltersBarProps {
    value: EventFiltersValue;
    onChange: (next: EventFiltersValue) => void;
    customerOptions: MultiSelectOption[];
    assigneeOptions: MultiSelectOption[];
    cityOptions: MultiSelectOption[];
    stateOptions: MultiSelectOption[];
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const EventFiltersBar: React.FC<EventFiltersBarProps> = ({
    value,
    onChange,
    customerOptions,
    assigneeOptions,
    cityOptions,
    stateOptions,
    className,
}) => {
    /** Lift a partial update onto the full filter state. */
    const update = (patch: Partial<EventFiltersValue>) => {
        onChange({ ...value, ...patch });
    };

    /* Are any filters currently active? "Active" means narrowed from the
     * default — both record types selected is the default, so a narrowed
     * record-type set (one or zero selected) counts as active. */
    const isRecordTypesNarrowed =
        value.recordTypes.size !== RECORD_TYPE_OPTIONS.length;
    const hasAnyFilter =
        value.dateRange.from !== '' ||
        value.dateRange.to !== '' ||
        value.customerIds.size > 0 ||
        value.assigneeIds.size > 0 ||
        value.cities.size > 0 ||
        value.states.size > 0 ||
        isRecordTypesNarrowed;

    /* Each narrowed dimension counts once, for the collapsed Filters button. */
    const activeCount = [
        value.dateRange.from !== '' || value.dateRange.to !== '',
        value.customerIds.size > 0,
        value.assigneeIds.size > 0,
        value.cities.size > 0,
        value.states.size > 0,
        isRecordTypesNarrowed,
    ].filter(Boolean).length;

    /* Below desktop the whole bar folds into a Filters button + pop-up so the
     * calendar / map keeps the screen. "Clear all" lives inside the bar, so
     * the pop-up doesn't add a second Reset. */
    return (
        <ResponsiveFilters
            title="Filter jobs"
            activeCount={activeCount}
            className={className ?? ''}
            triggerClassName={className ?? ''}
            dataRef="organism-event-filters-bar-wrapper"
        >
        <div className="event-filters-bar flex flex-col gap-12px" data-ref="organism-event-filters-bar">
            {/* Toggle row — extensible: drop additional FilterToggleGroup
             * blocks here as new boolean/categorical filters are added. */}
            <div className="event-filters-bar__groups flex flex-wrap gap-x-24px gap-y-12px">
                <FilterToggleGroup
                    label="Type"
                    options={RECORD_TYPE_OPTIONS}
                    selected={value.recordTypes}
                    onChange={(recordTypes) => update({ recordTypes })}
                />
            </div>

            {/* Existing date + multi-select row */}
            <div className="event-filters-bar__selects flex flex-col tablet:flex-row tablet:items-end flex-wrap gap-12px">
                <DateRangePicker
                    value={value.dateRange}
                    onChange={(dateRange) => update({ dateRange })}
                />

                <MultiSelectFilter
                    label="Customer"
                    options={customerOptions}
                    selectedValues={value.customerIds}
                    onChange={(customerIds) => update({ customerIds })}
                    placeholder="Search customers..."
                />

                <MultiSelectFilter
                    label="Tech"
                    options={assigneeOptions}
                    selectedValues={value.assigneeIds}
                    onChange={(assigneeIds) => update({ assigneeIds })}
                    placeholder="Search techs..."
                />

                <MultiSelectFilter
                    label="City"
                    options={cityOptions}
                    selectedValues={value.cities}
                    onChange={(cities) => update({ cities })}
                    placeholder="Search cities..."
                />

                <MultiSelectFilter
                    label="State"
                    options={stateOptions}
                    selectedValues={value.states}
                    onChange={(states) => update({ states })}
                    placeholder="Search states..."
                />

                {hasAnyFilter && (
                    <button
                        type="button"
                        onClick={() =>
                            onChange({
                                dateRange: { from: '', to: '' },
                                customerIds: new Set(),
                                assigneeIds: new Set(),
                                cities: new Set(),
                                states: new Set(),
                                recordTypes: new Set(RECORD_TYPE_OPTIONS.map((o) => o.value)),
                            })
                        }
                        className="event-filters-bar__clear inline-flex items-center min-h-44px desktop:min-h-0 text-13px font-sans text-brand-primary hover:underline self-center mt-4px"
                    >
                        Clear all
                    </button>
                )}
            </div>
        </div>
        </ResponsiveFilters>
    );
};

export default EventFiltersBar;
